import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Check, X } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { loadRateCards } from '@/lib/rateCards';

const INPUT = 'h-8 rounded-lg border border-input bg-background px-2 text-sm text-foreground';
const BASE = '/notes/tag-workspace/rate-cards';

function Row({ label, entry, onChange, testid }) {
  const conflict = entry.seen.length > 1;
  const empty = entry.rate === '' || entry.rate == null;
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-lg px-2 py-1 ${empty ? 'bg-rose-50' : conflict ? 'bg-amber-50' : ''}`} data-testid={empty ? `${testid}-unfilled` : testid}>
      <span className="min-w-0 flex-1 truncate font-mono text-xs">{label}</span>
      {conflict ? <span className="inline-flex items-center gap-1 text-[length:calc(var(--fs,1)*0.6875rem)] text-amber-700" data-testid={`${testid}-conflict`}><AlertTriangle size={12} /> {entry.seen.map((s) => `${s.v} ×${s.n}`).join(', ')}</span> : null}
      <input type="number" step="1" value={entry.rate ?? ''} placeholder="Fill rate" onChange={(e) => onChange(e.target.value)} className={`${INPUT} w-28`} data-testid={`${testid}-rate`} />
    </div>
  );
}

// Preview of rates read from an uploaded Excel → merge into a customer's rate card.
const blankEntry = () => ({ rate: '', seen: [] });
// Add every required / unfilled row (empty rate) so the user can fill it online.
function withRequired(p, req) {
  const lines = { ...p.lines };
  const miss = p.missing || [];
  [...(req?.lines || []), ...miss.filter((m) => m !== 'Labour' && !m.startsWith('Gold '))].forEach((k) => { if (!lines[k]) lines[k] = blankEntry(); });
  return { lines, labour: p.labour || blankEntry() };
}
const emptyFirst = (o) => Object.entries(o).sort(([, a], [, b]) => (a.rate === '' ? 0 : 1) - (b.rate === '' ? 0 : 1));

export function TagRateImport({ parsed, required, cards, onDone, onCancel }) {
  const [data, setData] = useState(() => withRequired(parsed, required));
  const [target, setTarget] = useState(cards[0]?.id || 'new');
  const [newName, setNewName] = useState('');
  const set = (kind, key, rate) => setData((d) => (kind === 'labour' ? { ...d, labour: { ...d.labour, rate } } : { ...d, [kind]: { ...d[kind], [key]: { ...d[kind][key], rate } } }));
  const vals = (o) => Object.fromEntries(Object.entries(o).filter(([, e]) => e.rate !== '' && e.rate != null).map(([k, e]) => [k, Math.round(Number(e.rate))]));
  const save = async () => {
    const card = cards.find((c) => c.id === target);
    if (!card && !newName.trim()) { toast.error('Name the new rate card.'); return; }
    const body = {
      name: card ? card.name : newName.trim(),
      lines: { ...(card?.lines || {}), ...vals(data.lines) },
      gold: {},
      labour: data.labour && data.labour.rate !== '' ? Math.round(Number(data.labour.rate)) : (card?.labour ?? null),
    };
    try {
      if (card) await notesApi.put(`${BASE}/${card.id}`, body); else await notesApi.post(BASE, body);
      toast.success(`Rates saved to "${body.name}".`); loadRateCards(); onDone();
    } catch (e) { toast.error(extractError(e, 'Could not save the rates.')); }
  };
  const all = [...Object.values(data.lines), data.labour];
  const nLines = all.filter((e) => e.rate !== '' && e.rate != null).length;
  const unfilled = all.length - nLines;
  return (
    <div className="space-y-3 rounded-xl border border-[hsl(var(--ring))] bg-background p-3" data-testid="tagws-rate-import-preview">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Rates in the file: {nLines} filled{unfilled ? <span className="ml-1 text-rose-700" data-testid="tagws-rate-import-unfilled-count">· {unfilled} not filled (pink — fill them below)</span> : null}</span>
        <button onClick={onCancel} aria-label="Cancel import" className="text-muted-foreground" data-testid="tagws-rate-import-cancel"><X size={16} /></button>
      </div>
      <div className="max-h-80 space-y-0.5 overflow-y-auto">
        <Row label="Labour (per g)" entry={data.labour} onChange={(v) => set('labour', null, v)} testid="tagws-rate-import-labour" />
        {emptyFirst(data.lines).map(([k, e]) => <Row key={`l${k}`} label={k} entry={e} onChange={(v) => set('lines', k, v)} testid="tagws-rate-import-line" />)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Save into</span>
        <select value={target} onChange={(e) => setTarget(e.target.value)} className={INPUT} data-testid="tagws-rate-import-target">
          {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          <option value="new">+ New rate card</option>
        </select>
        {target === 'new' ? <input value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={80} placeholder="Customer name" className={`${INPUT} w-48`} data-testid="tagws-rate-import-new-name" /> : null}
        <button onClick={save} disabled={!nLines} className="ml-auto inline-flex h-8 items-center gap-1 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground disabled:opacity-50" data-testid="tagws-rate-import-save"><Check size={13} /> Save rates</button>
      </div>
      <p className="text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">Only filled rates are saved (whole rupees); rows left empty keep the card's existing rate.</p>
    </div>
  );
}
